import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Plus, Edit, Trash2, Sparkles, Loader2 } from 'lucide-react'
import { useJobListings, useCareerPage } from '@/hooks/use-career-page'
import { JobListing } from '@/services/career-service'

const languages = [
    { code: 'tr', label: 'Türkçe' },
    { code: 'en', label: 'English' },
    { code: 'nl', label: 'Dutch' },
    { code: 'fr', label: 'Français' },
    { code: 'ar', label: 'العربية' },
    { code: 'ru', label: 'Русский' },
    { code: 'es', label: 'Español' }
]

export default function KariyerModule() {
    const { data: jobs = [], createJob, updateJob, deleteJob, isLoading: jobsLoading } = useJobListings()
    const { data: serverPageContent, updateContent, isLoading: contentLoading } = useCareerPage()

    const [activeTab, setActiveTab] = useState('listings')
    const [editingJob, setEditingJob] = useState<Partial<JobListing> | null>(null)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [selectedLang, setSelectedLang] = useState('tr')
    const [contentLang, setContentLang] = useState('tr')
    const [isTranslating, setIsTranslating] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isTranslatingContent, setIsTranslatingContent] = useState(false)

    const [pageContent, setPageContent] = useState<any>(null)
    const [isSavingContent, setIsSavingContent] = useState(false)
    const [lastServerData, setLastServerData] = useState<any>(null)

    useEffect(() => {
        if (serverPageContent && serverPageContent !== lastServerData) {
            setPageContent(serverPageContent)
            setLastServerData(serverPageContent)
        }
    }, [serverPageContent, lastServerData])

    if (jobsLoading || contentLoading) {
        return (
            <div className="flex h-[80vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    const handleSaveJob = async () => {
        if (!editingJob) return

        setIsSaving(true)
        try {
            if (editingJob.id) {
                await updateJob({ id: editingJob.id, data: editingJob, translate: isTranslating })
            } else {
                await createJob({ data: editingJob, translate: isTranslating })
            }
            setIsDialogOpen(false)
            setEditingJob(null)
        } catch (error) {
            console.error('Save job error:', error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleUpdateContent = (newContent: any) => {
        setPageContent(newContent)
    }

    const handleSaveContent = async () => {
        if (!pageContent) return
        setIsSavingContent(true)
        try {
            await updateContent({ data: pageContent })
        } catch (error) {
            console.error('Update content error:', error)
        } finally {
            setIsSavingContent(false)
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-background z-10 sticky top-0 py-2 pb-4 border-b mb-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Kariyer Yönetimi</h1>
                    <p className="text-sm text-muted-foreground">İş ilanlarını ve kariyer sayfası içeriğini yönetin</p>
                </div>
                {activeTab === 'content' && (
                    <Button onClick={handleSaveContent} disabled={isSavingContent} className="mt-4 sm:mt-0 shadow-lg" size="lg">
                        {isSavingContent && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Sayfayı Güncelle
                    </Button>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList>
                    <TabsTrigger value="listings">İş İlanları</TabsTrigger>
                    <TabsTrigger value="content">Sayfa İçeriği</TabsTrigger>
                </TabsList>

                <TabsContent value="listings" className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold">İş İlanları Yönetimi</h2>
                        <Button onClick={() => { setEditingJob({ status: 'active' }); setSelectedLang('tr'); setIsDialogOpen(true) }}>
                            <Plus className="mr-2 h-4 w-4" /> Yeni İlan
                        </Button>
                    </div>

                    <Card>
                        <CardContent className="pt-6">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Başlık</TableHead>
                                        <TableHead>Departman</TableHead>
                                        <TableHead>Lokasyon</TableHead>
                                        <TableHead>Tür</TableHead>
                                        <TableHead>Durum</TableHead>
                                        <TableHead className="text-right">İşlemler</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {jobs.map((job) => (
                                        <TableRow key={job.id}>
                                            <TableCell className="font-medium text-sm">{(job.title as any)?.tr || '-'}</TableCell>
                                            <TableCell>{(job.department as any)?.tr || '-'}</TableCell>
                                            <TableCell>{(job.location as any)?.tr || '-'}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{(job.type as any)?.tr || '-'}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={job.status === 'active' ? 'default' : 'secondary'}>
                                                    {job.status === 'active' ? 'Aktif' : 'Kapalı'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button variant="ghost" size="icon" onClick={() => { setEditingJob(job); setSelectedLang('tr'); setIsDialogOpen(true) }}>
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteJob(job.id)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="content" className="space-y-4">
                    {pageContent && (
                        <div className="grid gap-6">
                            <Tabs value={contentLang} onValueChange={setContentLang} className="w-full mb-2">
                                <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                                    {languages.map(lang => (
                                        <TabsTrigger
                                            key={lang.code}
                                            value={lang.code}
                                            className="text-xs px-4 py-1.5 flex-shrink-0"
                                        >
                                            {lang.label}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </Tabs>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Hero Bölümü</CardTitle>
                                    <CardDescription>Kariyer sayfasının üst kısmındaki başlık ve alt başlık.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Başlık ({contentLang.toUpperCase()})</Label>
                                        <Input
                                            value={(pageContent.hero.title as any)[contentLang] || ''}
                                            onChange={(e) => handleUpdateContent({
                                                ...pageContent,
                                                hero: { ...pageContent.hero, title: { ...pageContent.hero.title, [contentLang]: e.target.value } }
                                            })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Alt Başlık ({contentLang.toUpperCase()})</Label>
                                        <Textarea
                                            value={(pageContent.hero.subtitle as any)[contentLang] || ''}
                                            onChange={(e) => handleUpdateContent({
                                                ...pageContent,
                                                hero: { ...pageContent.hero, subtitle: { ...pageContent.hero.subtitle, [contentLang]: e.target.value } }
                                            })}
                                        />
                                    </div>
                                    <Button
                                        className="w-fit"
                                        disabled={isTranslatingContent}
                                        onClick={async () => {
                                            setIsTranslatingContent(true)
                                            try {
                                                await updateContent({ data: pageContent, translate: true })
                                            } finally {
                                                setIsTranslatingContent(false)
                                            }
                                        }}
                                    >
                                        {isTranslatingContent ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                                        Tüm Dillere Çevir
                                    </Button>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Özellikler (Features)</CardTitle>
                                    <CardDescription>Çalışma ortamı, sosyal haklar vb. bölümler.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {pageContent.features.map((feature: any, idx: number) => (
                                        <div key={idx} className="p-4 border rounded-lg space-y-4 relative">
                                            <Button size="icon" variant="ghost" className="absolute right-2 top-2 text-destructive">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>İkon</Label>
                                                    <Input value={feature.icon} readOnly className="bg-muted" />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Başlık ({contentLang.toUpperCase()})</Label>
                                                    <Input
                                                        value={(feature.title as any)[contentLang] || ''}
                                                        onChange={(e) => {
                                                            const newFeatures = [...pageContent.features]
                                                            newFeatures[idx] = {
                                                                ...feature,
                                                                title: { ...feature.title, [contentLang]: e.target.value }
                                                            }
                                                            handleUpdateContent({ ...pageContent, features: newFeatures })
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Açıklama ({contentLang.toUpperCase()})</Label>
                                                <Textarea
                                                    value={(feature.description as any)[contentLang] || ''}
                                                    onChange={(e) => {
                                                        const newFeatures = [...pageContent.features]
                                                        newFeatures[idx] = {
                                                            ...feature,
                                                            description: { ...feature.description, [contentLang]: e.target.value }
                                                        }
                                                        handleUpdateContent({ ...pageContent, features: newFeatures })
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </TabsContent>

                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editingJob?.id ? 'İlanı Düzenle' : 'Yeni İlan Oluştur'}</DialogTitle>
                            <DialogDescription>İş ilanı detaylarını tüm diller için girin veya AI ile çevirin.</DialogDescription>
                        </DialogHeader>

                        <div className="space-y-6 py-4">
                            <Tabs value={selectedLang} onValueChange={setSelectedLang} className="w-full">
                                <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                                    {languages.map(lang => (
                                        <TabsTrigger
                                            key={lang.code}
                                            value={lang.code}
                                            className="text-xs px-4 py-1.5 flex-shrink-0"
                                        >
                                            {lang.label}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </Tabs>

                            <div className="grid gap-4">
                                <div className="space-y-2">
                                    <Label>İlan Başlığı ({selectedLang})</Label>
                                    <Input
                                        value={(editingJob?.title as any)?.[selectedLang] || ''}
                                        onChange={(e) => setEditingJob(prev => ({
                                            ...prev!,
                                            title: { ...(prev?.title as any || {}), [selectedLang]: e.target.value }
                                        }))}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Departman ({selectedLang})</Label>
                                        <Input
                                            value={(editingJob?.department as any)?.[selectedLang] || ''}
                                            onChange={(e) => setEditingJob(prev => ({
                                                ...prev!,
                                                department: { ...(prev?.department as any || {}), [selectedLang]: e.target.value }
                                            }))}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Tür (Tam Zamanlı vb.) ({selectedLang})</Label>
                                        <Input
                                            value={(editingJob?.type as any)?.[selectedLang] || ''}
                                            onChange={(e) => setEditingJob(prev => ({
                                                ...prev!,
                                                type: { ...(prev?.type as any || {}), [selectedLang]: e.target.value }
                                            }))}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Lokasyon ({selectedLang})</Label>
                                        <Input
                                            value={(editingJob?.location as any)?.[selectedLang] || ''}
                                            onChange={(e) => setEditingJob(prev => ({
                                                ...prev!,
                                                location: { ...(prev?.location as any || {}), [selectedLang]: e.target.value }
                                            }))}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Deneyim ({selectedLang})</Label>
                                        <Input
                                            value={(editingJob?.experience as any)?.[selectedLang] || ''}
                                            onChange={(e) => setEditingJob(prev => ({
                                                ...prev!,
                                                experience: { ...(prev?.experience as any || {}), [selectedLang]: e.target.value }
                                            }))}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Açıklama ({selectedLang})</Label>
                                    <Textarea
                                        rows={6}
                                        value={(editingJob?.description as any)?.[selectedLang] || ''}
                                        onChange={(e) => setEditingJob(prev => ({
                                            ...prev!,
                                            description: { ...(prev?.description as any || {}), [selectedLang]: e.target.value }
                                        }))}
                                    />
                                </div>
                                <div className="flex items-center space-x-2 pt-4 border-t">
                                    <Label>Durum</Label>
                                    <Select
                                        value={editingJob?.status}
                                        onValueChange={(v: any) => setEditingJob(prev => ({ ...prev!, status: v }))}
                                    >
                                        <SelectTrigger className="w-[120px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="active">Aktif</SelectItem>
                                            <SelectItem value="closed">Kapalı</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        <DialogFooter className="flex items-center justify-between sm:justify-between">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="translate"
                                    checked={isTranslating}
                                    onChange={(e) => setIsTranslating(e.target.checked)}
                                />
                                <Label htmlFor="translate" className="cursor-pointer text-sm font-normal">Diğer dillere AI ile çevir</Label>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>İptal</Button>
                                <Button disabled={isSaving} onClick={handleSaveJob}>
                                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Kaydet
                                </Button>
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </Tabs>
        </div>
    )
}
